/**
 * GlobalSearchBar — TekStak wiring for the shared @techstack/ui
 * GlobalSearch omnibox (Phase 1 of docs/UNIVERSAL-SEARCH-ROADMAP.md).
 *
 * Data: /api/v1/search + /suggest via apiClient (Supabase JWT; the
 * backend maps the user to their linked API key for quote visibility —
 * users without a linked key still search public data).
 *
 * Navigation: maps the backend's route payload ({ view, ...params })
 * onto react-router paths. Department hits also drive the global
 * department filter via onDepartmentSelect so the whole app follows.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalSearch } from '@techstack/ui';
import apiClient from '../../lib/apiClient';
import PoDetailModal from '../PoDetailModal/PoDetailModal';
import '@techstack/ui/dist/styles/global-search.css';

const GlobalSearchBar = ({ onDepartmentSelect }) => {
  const navigate = useNavigate();
  // PO hits open the app-wide PoDetailModal in place rather than routing
  // away — same modal Triage, FlagReview, and Vendor Analysis use.
  const [poModal, setPoModal] = useState(null);

  const fetchSuggest = useCallback(
    (q) => apiClient.get('/api/v1/search/suggest', { params: { q } }),
    [],
  );
  const fetchSearch = useCallback(
    (q) => apiClient.get('/api/v1/search', { params: { q } }),
    [],
  );
  const logClick = useCallback((hit, rank, query) => {
    apiClient
      .post('/api/v1/search/click', {
        query,
        entityType: hit.entityType,
        entityId: hit.entityId,
        rank,
      })
      .catch((err) => console.warn('[global-search] click log failed:', err?.message));
  }, []);

  const onNavigate = useCallback(
    (hit) => {
      const route = hit.route || {};
      switch (route.view) {
        case 'department':
          if (route.deptId) onDepartmentSelect?.(route.deptId);
          navigate('/workspace');
          break;
        case 'account':
          navigate('/workspace');
          break;
        case 'contact':
          navigate('/workspace');
          break;
        case 'vendor':
          navigate(`/vendor-analysis?vendor=${encodeURIComponent(route.vendor ?? '')}`);
          break;
        case 'quote':
          navigate(`/quotes?quoteId=${encodeURIComponent(route.quoteId ?? '')}`);
          break;
        case 'contract':
          navigate(`/quotes?contractId=${encodeURIComponent(route.contractId ?? '')}`);
          break;
        case 'opportunity':
          navigate(`/opportunities/${encodeURIComponent(route.opportunityId ?? '')}`);
          break;
        case 'solicitation':
          navigate(`/solicitations/${encodeURIComponent(route.solicitationId ?? '')}`);
          break;
        case 'component':
          navigate(`/products?componentId=${encodeURIComponent(route.componentId ?? '')}`);
          break;
        case 'po':
          // Open the PO in place. deptId disambiguates the same PO# across
          // departments; date scopes to the right generation of a reused PO#.
          if (route.documentNumber) {
            setPoModal({
              documentNumber: route.documentNumber,
              departmentId: route.deptId ?? null,
              startDate: route.date ?? null,
            });
          }
          break;
        case 'sku':
          navigate(`/quotes?sku=${encodeURIComponent(route.sku ?? '')}`);
          break;
        default:
          navigate('/home');
      }
    },
    [navigate, onDepartmentSelect],
  );

  return (
    <>
      <GlobalSearch
        fetchSuggest={fetchSuggest}
        fetchSearch={fetchSearch}
        onNavigate={onNavigate}
        logClick={logClick}
        placeholder={
          // No keyboard shortcut hint on touch devices — ⌘K means nothing
          // to a thumb and eats placeholder width on phones.
          typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches
            ? 'Search everything…'
            : 'Search everything…  ⌘K'
        }
      />
      {poModal && (
        <PoDetailModal
          documentNumber={poModal.documentNumber}
          departmentId={poModal.departmentId}
          startDate={poModal.startDate}
          isOpen
          onClose={() => setPoModal(null)}
        />
      )}
    </>
  );
};

export default GlobalSearchBar;
